import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// EMAIL QUEUE — the Super Admin's view of the outbound mail queue
// (/api/platform/email-queue/*, requireSuperAdmin on the API). No catalogue
// key exists for it, so the page sits behind SuperAdminRoute like Settings.
//
// `silent` on the two reads: the page refreshes every 15 s while open, and a
// background tick must not flash the loader over the table — only a request
// the user asked for (first load, tab change, manual refresh) shows it.

const QUEUE_URL = `${baseURL}/platform/email-queue`;

// Helper function to extract serializable error information
const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        // The API answers `{ success:false, message: { en } }` (emailQueueController#fail);
        // unwrap it so the reason reaches the toast and state.error, never an object.
        const msg = error.response.data?.message;
        return (msg && typeof msg === 'object' ? msg.en : msg)
            || error.message
            || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

const errorMessage = (action, fallback) =>
    (typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || fallback));

// GET /stats
export const getEmailQueueStats = createAsyncThunk(
    'emailQueue/getEmailQueueStats',
    async (_args, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${QUEUE_URL}/stats`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// GET /jobs?state=failed|waiting|delayed|active|completed&page&limit
export const getEmailQueueJobs = createAsyncThunk(
    'emailQueue/getEmailQueueJobs',
    async ({ state = 'failed', page = 1, limit = 10 } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${QUEUE_URL}/jobs`, { params: { state, page, limit } });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// POST /jobs/:id/retry
export const retryEmailJob = createAsyncThunk(
    'emailQueue/retryEmailJob',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${QUEUE_URL}/jobs/${encodeURIComponent(id)}/retry`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// DELETE /jobs/:id
export const removeEmailJob = createAsyncThunk(
    'emailQueue/removeEmailJob',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${QUEUE_URL}/jobs/${encodeURIComponent(id)}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// POST /retry-all-failed → data: { retried }
export const retryAllFailedEmails = createAsyncThunk(
    'emailQueue/retryAllFailedEmails',
    async (_args, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${QUEUE_URL}/retry-all-failed`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// POST /test body { to } → data: { jobId, mode: 'queued' | 'direct' }
export const sendTestEmail = createAsyncThunk(
    'emailQueue/sendTestEmail',
    async ({ to }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${QUEUE_URL}/test`, { to });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const emailQueueSlice = createSlice({
    name: 'emailQueue',
    initialState: {
        stats: null,
        statsLoading: false,
        jobs: [],
        jobsTotalItems: 0,
        jobsLoading: false,
        // requestId of the newest getEmailQueueJobs dispatch. A background tick
        // and a tile click can be in flight together; only the newest may write
        // the table, otherwise a slow earlier response overwrites the newer one.
        jobsRequestId: null,
        // Id of the job whose Retry / Remove is in flight, or 'all' for the
        // bulk retry — the row disables its own buttons, not every row's.
        mutatingId: null,
        testSending: false,
        error: null,
    },
    reducers: {
        clearEmailQueueError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getEmailQueueStats.pending, (state, action) => {
                if (!action.meta.arg?.silent) state.statsLoading = true;
                state.error = null;
            })
            .addCase(getEmailQueueStats.fulfilled, (state, action) => {
                state.statsLoading = false;
                state.stats = action.payload?.data || null;
            })
            .addCase(getEmailQueueStats.rejected, (state, action) => {
                state.statsLoading = false;
                state.error = errorMessage(action, 'Failed to load the email queue status');
            })
            .addCase(getEmailQueueJobs.pending, (state, action) => {
                state.jobsRequestId = action.meta.requestId;
                if (!action.meta.arg?.silent) state.jobsLoading = true;
                state.error = null;
            })
            .addCase(getEmailQueueJobs.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.jobsRequestId) return; // stale — a newer request is in flight or already landed
                state.jobsLoading = false;
                state.jobs = action.payload?.data || [];
                state.jobsTotalItems = action.payload?.totalItems || 0;
            })
            .addCase(getEmailQueueJobs.rejected, (state, action) => {
                if (action.meta.requestId !== state.jobsRequestId) return;
                state.jobsLoading = false;
                state.error = errorMessage(action, 'Failed to load the email queue jobs');
            })
            .addCase(retryEmailJob.pending, (state, action) => {
                state.mutatingId = action.meta.arg;
            })
            .addCase(retryEmailJob.fulfilled, (state) => {
                state.mutatingId = null;
            })
            .addCase(retryEmailJob.rejected, (state, action) => {
                state.mutatingId = null;
                state.error = errorMessage(action, 'Failed to retry the email');
            })
            .addCase(removeEmailJob.pending, (state, action) => {
                state.mutatingId = action.meta.arg;
            })
            .addCase(removeEmailJob.fulfilled, (state, action) => {
                state.mutatingId = null;
                state.jobs = state.jobs.filter((job) => String(job.id) !== String(action.meta.arg));
                state.jobsTotalItems = Math.max(0, state.jobsTotalItems - 1);
            })
            .addCase(removeEmailJob.rejected, (state, action) => {
                state.mutatingId = null;
                state.error = errorMessage(action, 'Failed to remove the email');
            })
            .addCase(retryAllFailedEmails.pending, (state) => {
                state.mutatingId = 'all';
            })
            .addCase(retryAllFailedEmails.fulfilled, (state) => {
                state.mutatingId = null;
            })
            .addCase(retryAllFailedEmails.rejected, (state, action) => {
                state.mutatingId = null;
                state.error = errorMessage(action, 'Failed to retry the failed emails');
            })
            .addCase(sendTestEmail.pending, (state) => {
                state.testSending = true;
            })
            .addCase(sendTestEmail.fulfilled, (state) => {
                state.testSending = false;
            })
            .addCase(sendTestEmail.rejected, (state, action) => {
                state.testSending = false;
                state.error = errorMessage(action, 'Failed to send the test email');
            });
    },
});

export const { clearEmailQueueError } = emailQueueSlice.actions;
export default emailQueueSlice.reducer;
